import { SportType } from '../models/court/dto/court.dto';

/**
 * Bounds for a bookable game capacity. Mirrors `MIN/MAX_BOOKING_CAPACITY` in
 * `api/src/court-pricing/sport-capacity.ts`, which every money path enforces.
 * A capacity outside this range cannot become a booking, so the dashboard must
 * not let an operator configure or request one — the failure would otherwise
 * only surface as a 400 when a player tries to pay.
 */
export const MIN_BOOKING_CAPACITY = 1;
export const MAX_BOOKING_CAPACITY = 20;

/**
 * Sentinel stored in a pricing row's `capacity` for a row that prices every
 * game capacity. It is the column default, so every row written before
 * multi-capacity pricing carries it. Mirrors `WILDCARD_CAPACITY` in
 * `api/src/court/entity/court-availability-pricing.entity.ts`.
 */
export const WILDCARD_CAPACITY = -1;

/**
 * The `default:` branch of the pre-picker client capacity tables. Mirrors
 * `LEGACY_CLIENT_FALLBACK_CAPACITY` in
 * `api/src/court-pricing/sport-capacity.ts`.
 *
 * Not an accepted capacity on its own — see {@link legacyClientCapacity}, which
 * is what the server's rule 4 actually accepts alongside the sport's legacy
 * capacity.
 */
export const LEGACY_CLIENT_FALLBACK_CAPACITY = 4;

/** True when `capacity` is a whole number a booking may actually be made at. */
export function isBookableCapacity(capacity: unknown): capacity is number {
    return (
        typeof capacity === 'number' &&
        Number.isInteger(capacity) &&
        capacity >= MIN_BOOKING_CAPACITY &&
        capacity <= MAX_BOOKING_CAPACITY
    );
}

/**
 * The legacy, hardcoded number of players for a sport.
 *
 * **This table must stay identical to `sportTypeToCapacity` in
 * `api/src/court-pricing/sport-capacity.ts`** — see `sport-capacity.test.ts`,
 * which pins every case. The API uses it as the fallback capacity whenever an
 * organization has configured no capacity options for the sport, and as the
 * capacity of a booking that sends none. The mobile app shipped a diverging
 * copy of this table for months and it mis-charged eight sports (the app showed
 * `base / 2` for squash while creating a 4-seat game charged at `base / 4`);
 * that divergence was closed in Phase 4 and this file must not reopen it.
 *
 * The dashboard needs it because the admin-booking modal has to pick a capacity
 * for the operator, and hardcoding 4 — which it did — creates a tennis game
 * with 4 seats that later charges each joiner half of what they owe.
 */
export function sportTypeToCapacity(sportType: SportType): number {
    switch (sportType) {
        case SportType.PADEL:
            return 4;
        case SportType.TENNIS:
            return 2;
        case SportType.BASKETBALL:
            return 10;
        case SportType.BADMINTON:
            return 2;
        case SportType.PADEL1V1:
            return 2;
        case SportType.BASKETBALL1V1:
            return 2;
        case SportType.BEACH_RUGBY:
            return 10;
        case SportType.BEACH_SOCCER:
            return 10;
        case SportType.BEACH_TENNIS:
            return 4;
        case SportType.BEACH_VOLLEYBALL:
            return 4;
        case SportType.BEACH_BADMINTON:
            return 4;
        case SportType.GOLF:
            return 4;
        case SportType.FOOTBALL:
            return 10;
        case SportType.PICKLE_BALL:
            return 2;
        case SportType.SANDBALL:
            return 10;
        case SportType.SQUASH:
            return 2;
        case SportType.TENNIS_DE_TABLE:
            return 2;
        case SportType.TOUCH_TENNIS:
            return 2;
        default:
            return 4;
    }
}

/**
 * The capacity a **shipped pre-picker client actually sends** for `sport`.
 *
 * **Must stay identical to `legacyClientCapacity` in
 * `api/src/court-pricing/sport-capacity.ts`** — it is half of what the server's
 * rule 4 accepts when an organization has configured no capacity options, so a
 * dashboard that offers more than this produces a 400 the operator cannot act
 * on, and one that offers less hides a capacity that is genuinely bookable.
 *
 * It is the OLD mobile table (the one Phase 4 replaced): PADEL 4, TENNIS 2,
 * BASKETBALL 10, BADMINTON 2, PADEL1V1 2, BASKETBALL1V1 2, everything else 4.
 * A flat 4 for every sport — which this file used to mirror — let a client pick
 * its own per-person divisor on the legacy-2 sports it never actually sent 4
 * for.
 */
export function legacyClientCapacity(sport: SportType): number {
    switch (sport) {
        case SportType.PADEL:
            return 4;
        case SportType.TENNIS:
            return 2;
        case SportType.BASKETBALL:
            return 10;
        case SportType.BADMINTON:
            return 2;
        case SportType.PADEL1V1:
            return 2;
        case SportType.BASKETBALL1V1:
            return 2;
        default:
            return LEGACY_CLIENT_FALLBACK_CAPACITY;
    }
}

/**
 * Every capacity a booking for `sport` can actually be made at, given what the
 * organization offers.
 *
 * Mirrors `resolveBookingCapacity`'s acceptance rules
 * (`api/src/court-pricing/service/calculate/resolve-booking-capacity.ts`), which
 * every money path enforces:
 *
 *  - options configured → the configured ones, **plus** the sport's legacy
 *    capacity (a deliberate compatibility hatch: an operator may configure
 *    options before their players have an app build with the picker);
 *  - nothing configured → the sport's legacy capacity and
 *    {@link legacyClientCapacity} only, which for tennis, badminton, both 1v1
 *    sports, basketball and the seven sports that already seat 4 is a single
 *    value.
 *
 * A pricing row at any other capacity is money nobody can spend, which is the
 * whole reason the dashboard needs this table: the two screens that produce the
 * state — availability prices and game capacity options — never looked at each
 * other, so the operator could only discover it from an empty booking grid.
 */
export function resolveBookableCapacities(
    sport: SportType,
    offeredCapacities: number[],
): number[] {
    const legacyCapacity = sportTypeToCapacity(sport);

    // Which rule applies is decided by whether ANY option row exists, exactly
    // as the server decides it: `getConfiguredCapacityOptions` does not filter
    // by range, so a single stale out-of-range option already moves the
    // organization off the unconfigured rule. Filtering here instead would
    // report an organization as unconfigured when the server treats it as
    // configured, and every warning drawn from that would be wrong.
    const bookable =
        offeredCapacities.length > 0
            ? [...offeredCapacities.filter(isBookableCapacity), legacyCapacity]
            : [legacyCapacity, legacyClientCapacity(sport)];

    return [...new Set(bookable)].sort((a, b) => a - b);
}

/**
 * Every capacity the ADMIN BOOKING MODAL may OFFER an operator, for one sport.
 *
 * **Deliberately NOT {@link resolveBookableCapacities}**, and the difference is
 * one value that costs real money.
 *
 * `resolveBookableCapacities` answers "what will the server ACCEPT", which is
 * the right question for the two warning surfaces that use it: a pricing row at
 * {@link legacyClientCapacity} IS spendable, because a shipped pre-picker mobile
 * build really does send that number, so a warning that called such a row
 * unbookable would be wrong.
 *
 * It is the wrong question for a dropdown. On an organization with NO capacity
 * options, rule 4 accepts two values — the sport's own capacity and the one a
 * pre-picker build sends — and for the eight sports where those differ
 * (SQUASH, PICKLE_BALL, TENNIS_DE_TABLE, TOUCH_TENNIS, FOOTBALL, SANDBALL,
 * BEACH_RUGBY, BEACH_SOCCER) `resolveBookingCapacityDetailed`'s
 * `withoutConfiguration` branch then returns `capacity !== pricingCapacity`:
 * the booking is SEATED at what was asked for and PRICED at the sport's own
 * capacity (D30). That split exists so a pre-picker build is charged the amount
 * it was shown. Offering it as a CHOICE turns a compatibility hatch into a
 * mispricing lever:
 *
 * > SQUASH, no options, €40 wildcard court, PUBLIC. Operator picks "4 joueurs".
 * > The payer is quoted and charged `40/2` = **€20.00**, `Game.capacity` is
 * > stamped **4**, and `gameJoiningCapacity` bills each of the three joiners
 * > `40/4` = **€10.00**. The venue collects **€50.00 on a €40.00 court** and two
 * > players in the same game pay different prices for identical seats. FOOTBALL
 * > runs the other way (priced at 10, seated at 4): **€34.00**, a 15 % loss.
 *
 * D30's charging rule is deliberate and is NOT changed here — it is what a
 * pre-picker build depends on, and the amendment recorded against it (Run 147)
 * is a blocked product question. What this function fixes is the reach: the
 * dashboard is REDEPLOYED rather than installed, so unlike mobile — whose
 * picker cannot render for an unconfigured organization — it was a live,
 * current-build entrance into that split, one dropdown click wide.
 *
 * A CONFIGURED organization is unaffected: rule 3 never remaps, so every value
 * this returns there has `capacity === pricingCapacity`.
 */
export function resolveOfferableAdminBookingCapacities(
    sport: SportType,
    offeredCapacities: number[],
): number[] {
    if (offeredCapacities.length > 0) {
        return resolveBookableCapacities(sport, offeredCapacities);
    }

    return [sportTypeToCapacity(sport)];
}

/**
 * Every capacity a booking's PRICE can actually be looked up at, for one sport,
 * given what the organization offers.
 *
 * **Not the same set as {@link resolveBookableCapacities}**, and the difference
 * is D30. On an organization that has configured nothing, rule 4 ACCEPTS two
 * values — the sport's legacy capacity and {@link legacyClientCapacity} — but
 * `resolveBookingCapacity` then prices the second one at the FIRST: only a
 * pre-picker client sends it, that client quoted with no capacity at all, and
 * charging it at its own number is the quote-vs-charge divergence D30 exists to
 * close. So a squash block priced at capacity 2 and nothing else prices a
 * pre-picker `capacity: 4` **booking** perfectly well.
 *
 * With options configured the two sets coincide: the capacity is a real choice,
 * it selects a real row, and it is the divisor
 * (`resolve-booking-capacity.ts`, configured branch).
 *
 * Whether the organization counts as configured is decided by whether ANY
 * option row exists, unfiltered — exactly as {@link resolveBookableCapacities}
 * decides it, and for the same reason.
 *
 * **This is the BOOKER's set, and it is not the set of capacities a slot has to
 * have a price for.** The seat count the booker asked for is stamped on the
 * game and every JOINER is priced at it — see
 * {@link resolveChargeDivisorCapacities}.
 */
export function resolvePricingCapacities(
    sport: SportType,
    offeredCapacities: number[],
): number[] {
    const legacyCapacity = sportTypeToCapacity(sport);

    if (offeredCapacities.length === 0) {
        return [legacyCapacity];
    }

    return [
        ...new Set([
            ...offeredCapacities.filter(isBookableCapacity),
            legacyCapacity,
        ]),
    ].sort((a, b) => a - b);
}

/**
 * Every capacity a charge can be looked up and divided at, for one sport, given
 * what the organization offers.
 *
 * Mirrors the API's `chargeDivisorCapacities`
 * (`api/src/court-pricing/sport-capacity.ts`).
 *
 * **Wider than {@link resolvePricingCapacities}, because a booking bills more
 * than the person who made it.** D30 splits two numbers out of
 * `resolveBookingCapacity` and both reach money: the booker is priced at the
 * sport's legacy capacity, while the SEAT COUNT the client asked for is stamped
 * on `Game.capacity` and `gameJoiningCapacity` reads it straight back as the
 * row selector and the divisor for every joiner — quote and charge alike.
 *
 * So on an organization that has configured nothing, a squash block priced at
 * capacity 2 and nothing else does price a pre-picker `capacity: 4` booking,
 * and then has no row at all for anyone joining the 4-seat game it created.
 * Asking the booker's question calls that slot fully priced.
 *
 * With options configured the two sets coincide again: rule 3 rejects a
 * capacity outside the offered set, so it can be neither a price nor a seat.
 */
export function resolveChargeDivisorCapacities(
    sport: SportType,
    offeredCapacities: number[],
): number[] {
    return [
        ...new Set([
            ...resolvePricingCapacities(sport, offeredCapacities),
            ...resolveBookableCapacities(sport, offeredCapacities),
        ]),
    ].sort((a, b) => a - b);
}

/**
 * The capacity an admin booking should start on, given what the organization
 * offers for the sport.
 *
 * Mirrors the server's `pickDefaultCapacity`
 * (`court-booking-prices-by-capacity.ts`) so the modal pre-selects the same
 * capacity the player-facing picker would: the option flagged `isDefault`,
 * else the sport's legacy capacity when it is offered, else the lowest offered.
 * With nothing configured it is the legacy capacity — which is what the server
 * assumes when a client sends no capacity at all, so the two agree.
 */
export function resolveAdminBookingCapacity(
    sport: SportType,
    options: { capacity: number; isDefault: boolean }[],
): number {
    const legacyCapacity = sportTypeToCapacity(sport);

    const bookable = options
        .filter((option) => isBookableCapacity(option.capacity))
        .sort((a, b) => a.capacity - b.capacity);

    if (bookable.length === 0) {
        return legacyCapacity;
    }

    const flagged = bookable.find((option) => option.isDefault);
    if (flagged) {
        return flagged.capacity;
    }

    if (bookable.some((option) => option.capacity === legacyCapacity)) {
        return legacyCapacity;
    }

    return bookable[0].capacity;
}

/**
 * What an availability form's capacity field shows for one configuration.
 *
 * `undefined` (never filled in) and the `-1` sentinel (what the "add" button
 * seeds, and what the column default is) both mean "prices every capacity", so
 * both render as an EMPTY field — the same way every read-only surface shows a
 * wildcard row. Pair it with a placeholder naming that state
 * (`courts.all_capacities`), or the operator cannot tell "prices every
 * capacity" from "not filled in yet".
 *
 * The naive `config.capacity || ''` has two faults, and both were live. It
 * prints the literal `-1` for a wildcard row, which reads as a capacity of
 * minus one; and because these controls declare `min={MIN_BOOKING_CAPACITY}`
 * and Chakra's `NumberInput` clamps on blur by default, merely tabbing THROUGH
 * the field rewrites the wildcard row as a 1-player row without the operator
 * typing anything — silently turning a row that priced every capacity into one
 * that prices nobody but a solo booking.
 *
 * `0` must still render as `0`: it is not a wildcard, it is an unbookable
 * capacity, and the submit guard names it.
 *
 * Lives HERE, beside the bounds the same controls use, because it was
 * previously a private function in one of the two forms that need it — and the
 * other form kept the defective expression for exactly that reason.
 */
export function capacityFieldValue(
    capacity: number | undefined | null,
): string {
    if (
        capacity === undefined ||
        capacity === null ||
        capacity === WILDCARD_CAPACITY
    ) {
        return '';
    }

    return String(capacity);
}
